import { Suspense, lazy } from 'react';
import { MainLayout } from './components/MainLayout';
import { PublicLayout } from './components/PublicLayout';
import { ProfileLayout } from './components/profile/ProfileLayout';
import { Home } from './pages/Home';
import { Category } from './pages/Category';
import { Search } from './pages/Search';
import { Question } from './pages/Question';
import { QuoteCheckout } from './pages/reseller/QuoteCheckout';
import { CartCheckout } from './pages/cart/CartCheckout';

// Admin KB
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import { AdmQuestionList } from './pages/dashboard/AdmQuestionList';
import { AdmCategoryList } from './pages/dashboard/AdmCategoryList';
import { CategoryDetails } from './pages/dashboard/CategoryDetails';
import { QuestionDetails } from './pages/dashboard/QuestionDetails';
import { Settings } from './pages/dashboard/Settings';

import { LoadingScreen } from './components/LoadingScreen';

// Client profile
import { Checkout } from './pages/profile/Checkout';

// Reseller profile
import { ResellerCheckout } from './pages/reseller/ResellerCheckout';

const Loadable = (Component) => (props) => (
    <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
    </Suspense>
);

// Authentication pages
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const CreateAccount = Loadable(lazy(() => import('./pages/authentication/CreateAccount')));
const ForgotPassword = Loadable(lazy(() => import('./pages/authentication/ForgotPassword')));
const Register = Loadable(lazy(() => import('./pages/authentication/Register')));

// Profile pages
const Orders = Loadable(lazy(() => import('./pages/profile/Orders')));
const Profile = Loadable(lazy(() => import('./pages/profile/Profile')));
const Upgrades = Loadable(lazy(() => import('./pages/profile/Upgrades')));
const Discounts = Loadable(lazy(() => import('./pages/profile/Discounts')));
const Support = Loadable(lazy(() => import('./pages/profile/Support')));
const Invoice = Loadable(lazy(() => import('./pages/profile/Invoice')));
const SoftwareDownload = Loadable(lazy(() => import('./pages/profile/SoftwareDownload')));
const AddSupport = Loadable(lazy(() => import('./pages/profile/AddSupport')));
const Deactivate = Loadable(lazy(() => import('./pages/profile/Deactivate')));
const Upgrade = Loadable(lazy(() => import('./pages/profile/Upgrade')));
const Unsubscribe = Loadable(lazy(() => import('./pages/profile/Unsubscribe')));
const Unsubscribed = Loadable(lazy(() => import('./pages/profile/Unsubscribed')));

// Reseller profile
const ResellerProfile = Loadable(lazy(() => import('./pages/reseller/Profile')));
const ResellerOrders = Loadable(lazy(() => import('./pages/reseller/ResellerOrders')));
const ResellerNewOrder = Loadable(lazy(() => import('./pages/reseller/ResellerNewOrder')));

// Help Center pages
const SubmitRequest = Loadable(lazy(() => import('./pages/Submit')));
// const Category = Loadable(lazy(() => import('./pages/Category')));

// Error pages
const AuthorizationRequired = Loadable(lazy(() => import('./pages/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));
const Message = Loadable(lazy(() => import('./pages/Message')));

export const routes = [
    {
        path: '/account',
        children: [
            { path: 'login', element: <Login /> },
            { path: 'create-account', element: <CreateAccount /> },
            {
                path: 'request-sent',
                element: (
                    <Message
                        title="Request sent"
                        message="You'll get an email letting you know how to finilize profile registration."
                    />
                ),
            },
            {
                path: 'password-sent',
                element: (
                    <Message
                        title="Password sent"
                        message="You'll get an email with a password to sign in to your account."
                    />
                ),
            },
            { path: 'register', element: <Register /> },
            {
                path: 'verify-failed',
                element: (
                    <Message
                        title="Verification failed"
                        message="Registration URL is not correct or user has already been registered."
                    />
                ),
            },
            {
                path: 'registration-successful',
                element: (
                    <Message
                        title="Registration successful"
                        message="Registration successful. You can now sign in to your account."
                        linkText="Sign In"
                        linkUrl="/account/login"
                    />
                ),
            },
            { path: 'forgot-password', element: <ForgotPassword /> },
        ],
    },
    {
        path: '/client',
        element: <ProfileLayout />,
        children: [
            { path: 'orders', element: <Orders /> },
            { path: 'profile', element: <Profile /> },
            { path: 'upgrades', element: <Upgrades /> },
            { path: 'upgrades/:token', element: <Upgrade isPublic={false} /> },
            { path: 'discounts', element: <Discounts /> },
            { path: 'support', element: <Support /> },
            { path: 'invoices/:orderId', element: <Invoice isPublic={false} isReseller={false} /> },
            { path: 'downloads/:orderId', element: <SoftwareDownload isPublic={false} isReseller={false} /> },
            { path: 'add-support/:orderId', element: <AddSupport isPublic={false} isReseller={false} /> },
            { path: 'deactivate', element: <Deactivate /> },
            { path: 'checkout', element: <Checkout isPublic={false} /> },
        ],
    },
    {
        path: '/reseller',
        element: <ProfileLayout isReseller={true} />,
        children: [
            { path: 'orders', element: <ResellerOrders /> },
            { path: 'orders/new', element: <ResellerNewOrder /> },
            { path: 'profile', element: <ResellerProfile /> },
            { path: 'checkout', element: <ResellerCheckout /> },
            { path: 'invoices/:orderId', element: <Invoice isPublic={false} isReseller={true} /> },
            { path: 'downloads/:orderId', element: <SoftwareDownload isPublic={false} isReseller={true} /> },
            { path: 'add-support/:orderId', element: <AddSupport isPublic={false} isReseller={true} /> },
            { path: 'support', element: <Support /> },
        ],
    },
    {
        path: '/',
        element: <MainLayout />,
        children: [
            { path: '/', element: <Home /> },
            { path: '/categories/:id', element: <Category /> },
            { path: '/search', element: <Search /> },
            { path: '/requests/new', element: <SubmitRequest /> },
            { path: '/questions/:id', element: <Question /> },
            { path: 'load', element: <LoadingScreen /> },
            { path: '/unsubscribe', element: <Unsubscribe /> },
            { path: '/unsubscribed', element: <Unsubscribed /> },
        ],
    },
    {
        path: '/cart',
        element: <PublicLayout customTitle="Secure Order Processing" />,
        children: [{ path: 'checkout', element: <CartCheckout /> }],
    },
    {
        path: '/invoices',
        element: <PublicLayout />,
        children: [{ path: ':orderId', element: <Invoice isPublic={true} isReseller={false} /> }],
    },
    {
        path: '/quote-checkout',
        element: <PublicLayout />,
        children: [{ path: ':orderId', element: <QuoteCheckout /> }],
    },
    {
        path: '/downloads',
        element: <PublicLayout />,
        children: [{ path: ':orderId', element: <SoftwareDownload isPublic={true} isReseller={false} /> }],
    },
    {
        path: '/add-support',
        element: <PublicLayout />,
        children: [
            { path: ':orderId', element: <AddSupport isPublic={true} isReseller={false} /> },
            { path: 'checkout', element: <Checkout isPublic={true} /> },
            { path: 'upgrades/:token', element: <Upgrade isPublic={true} /> },
        ],
    },
    {
        path: '/kb',
        element: <DashboardLayout />,
        children: [
            { index: true, element: <AdmQuestionList /> },
            { path: 'categories', element: <AdmCategoryList /> },
            { path: 'categories/new', element: <CategoryDetails /> },
            { path: 'categories/:categoryId', element: <CategoryDetails /> },
            { path: 'questions/:questionId', element: <QuestionDetails /> },
            { path: 'questions/new', element: <QuestionDetails /> },
            { path: 'settings', element: <Settings /> },
        ],
    },
    {
        path: '401',
        element: <AuthorizationRequired />,
    },
    {
        path: '404',
        element: <NotFound />,
    },
    {
        path: '500',
        element: <ServerError />,
    },
    {
        path: 'clients',
        element: <Login />,
    },
    {
        path: '*',
        element: <NotFound />,
    },
];
